import { supabase } from "@/lib/supabase";
import type {
  IExchangeService,
  ExchangeOffer,
  CreateExchangeOffer,
  ExchangeStatus,
  ExchangeFilters,
  SkillMarketStat,
} from "@/services/interfaces/IExchangeService";

// The pool is cross-user, so the poster comes from public_profiles (the
// non-PII definer view), never from profiles, whose owner-or-admin RLS hides
// every other member. profiles has no name/city/country columns: the name is
// display_name, and location (location_city/location_country) is deliberately
// absent from public_profiles: it is released per quest through
// creator_read_released_address, so it is not shown here.
export const OFFER_SELECT =
  "*, profiles:public_profiles!exchange_offers_profile_id_fkey(display_name, avatar_url, handle)";

interface ExchangeRow {
  id: string;
  profile_id: string;
  i_offer: string;
  i_need: string;
  offer_skills: string[] | null;
  need_skills: string[] | null;
  category: string;
  modality: string;
  status: string;
  created_at: string;
  profiles: { display_name: string; avatar_url: string | null; handle: string | null } | null;
}

export function toOffer(row: ExchangeRow): ExchangeOffer {
  return {
    id: row.id,
    profileId: row.profile_id,
    iOffer: row.i_offer,
    iNeed: row.i_need,
    offerSkills: row.offer_skills ?? [],
    needSkills: row.need_skills ?? [],
    category: row.category as ExchangeOffer["category"],
    modality: (row.modality ?? "flexible") as ExchangeOffer["modality"],
    status: row.status as ExchangeOffer["status"],
    createdAt: row.created_at,
    profile: row.profiles
      ? {
          name: row.profiles.display_name,
          avatarUrl: row.profiles.avatar_url,
          handle: row.profiles.handle,
          city: null,
          country: null,
        }
      : undefined,
  };
}

/** Escape PostgREST filter metacharacters so user input can't inject predicates. */
function escapeFilterTerm(raw: string): string {
  return raw.replace(/[\\%_.,()]/g, (ch) => `\\${ch}`);
}

export class SupabaseExchangeService implements IExchangeService {
  async list(filters?: ExchangeFilters): Promise<ExchangeOffer[]> {
    let q = supabase
      .from("exchange_offers")
      .select(OFFER_SELECT)
      .eq("status", "open")
      .order("created_at", { ascending: false });

    if (filters?.category) q = q.eq("category", filters.category);
    if (filters?.search) {
      const safe = escapeFilterTerm(filters.search);
      const term = `%${safe}%`;
      q = q.or(`i_offer.ilike.${term},i_need.ilike.${term}`);
    }

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return ((data ?? []) as unknown as ExchangeRow[]).map(toOffer);
  }

  async listMine(): Promise<ExchangeOffer[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from("exchange_offers")
      .select(OFFER_SELECT)
      .eq("profile_id", user.id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return ((data ?? []) as unknown as ExchangeRow[]).map(toOffer);
  }

  async create(offer: CreateExchangeOffer): Promise<ExchangeOffer> {
    const { data, error } = await supabase
      .from("exchange_offers")
      .insert({
        i_offer: offer.iOffer,
        i_need: offer.iNeed,
        offer_skills: offer.offerSkills,
        need_skills: offer.needSkills,
        category: offer.category,
        modality: offer.modality,
      })
      .select(OFFER_SELECT)
      .single();
    if (error) throw new Error(error.message);
    return toOffer(data as unknown as ExchangeRow);
  }

  async setStatus(id: string, status: ExchangeStatus): Promise<void> {
    const { error } = await supabase
      .from("exchange_offers")
      .update({ status })
      .eq("id", id);
    if (error) throw new Error(error.message);
  }

  async remove(id: string): Promise<void> {
    const { error } = await supabase
      .from("exchange_offers")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  }

  async getSkillMarketStats(): Promise<SkillMarketStat[]> {
    const { data, error } = await supabase.rpc("get_skill_market_stats");
    if (error) throw new Error(error.message);
    return (data ?? []) as SkillMarketStat[];
  }
}
